import React, { useContext, useMemo } from "react";
import styled from "styled-components";
import intl from "react-intl-universal";
import { mdiContentDuplicate, mdiTrashCanOutline } from "@mdi/js";

import antennaRest from "../../../../rest/antennaRest";
import commonUtils from "../../../../script/utils/commonUtils";
import antennaUtils from "../../../../script/utils/antennaUtils";

import { useInput } from "../../../hooks/useInput";
import { useOnChange } from "../../../hooks/useOnChange";
import { useValidation } from "../../../hooks/useValidation";
import { EntryContext } from "../../../contexts/EntryContext";

import { Button } from "../../../components/Button";
import { Tooltip } from "../../../components/Tooltip";
import { Checkbox } from "../../../components/Checkbox";
import { InputText } from "../../../components/InputText";
import { InputNumber } from "../../../components/InputNumber";
import { SelectSingle } from "../../../components/SelectSingle";

const ActionsStyle = styled.td`
    white-space: nowrap;
    >div {
        display: flex;
        justify-content: flex-end;
    }
`;

// Tooltip centers its content, which would shrink the select to its text
const TooltipFieldStyle = styled.div`
    width: 100%;
`;

// inactive radios fade out, the checkbox and the actions stay readable
const RowStyle = styled.tr`
    &.disabled >td:not(:first-child):not(:last-child) {
        opacity: 0.5;
    }
`;

export const RadioItem = props => {

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;

    const { sector, radio, inputKey, minMaxDowntilt } = props;
    const systemName = useInput("systemName-" + inputKey, radio.systemName);
    const antennaPort = useInput("antennaPort-" + inputKey, radio.antennaPort);
    const antennaElecDowntilt = useInput("antennaElecDowntilt-" + inputKey, radio.antennaElecDowntilt);
    const antennaVirtual = useInput("antennaVirtual-" + inputKey, radio.antennaVirtual);
    const frequency = useInput("frequency-" + inputKey, radio.frequency);
    const numChannels = useInput("numChannels-" + inputKey, radio.numChannels);
    const antennaFrequency = useInput("antennaFrequency-" + inputKey, radio.antennaFrequency);
    const txPower = useInput("txPower-" + inputKey, radio.txPower);
    const attenuation = useInput("attenuation-" + inputKey, radio.attenuation);
    const enabled = useInput("enabled-" + inputKey, radio.enabled);

    const validationOptions = useMemo(() => ({ globalErrorMessage: intl.get("entry.tabs.radios") }), []);
    useValidation(value => !commonUtils.isBlank(value), [systemName], validationOptions);
    useValidation((value, virtual) => virtual || !commonUtils.isBlank(value), [antennaFrequency, antennaVirtual], validationOptions);
    useValidation((value, virtual) => virtual || !commonUtils.isBlank(value), [antennaPort, antennaVirtual], validationOptions);
    useValidation((value, virtual) => virtual || !commonUtils.isBlank(value), [antennaElecDowntilt, antennaVirtual], validationOptions);
    useValidation(value => !commonUtils.isBlank(value) && !isNaN(value.replace(",", ".")), [frequency], validationOptions);
    useValidation(value => !commonUtils.isBlank(value) && !isNaN(value.replace(",", ".")), [numChannels], validationOptions);
    useValidation(value => !commonUtils.isBlank(value) && !isNaN(value.replace(",", ".")), [txPower], validationOptions);
    useValidation(value => !commonUtils.isBlank(value) && !isNaN(value.replace(",", ".")), [attenuation], validationOptions);
    useValidation(value => !commonUtils.isBlank(value), [enabled], validationOptions);

    // the objects themselves too: saving replaces them, and the buttons below must act on the current ones
    const rerenderWhenChanged = [
        radio,
        sector,
        radio.value,
        sector.antennaName,
        systemName.value,
        systemName.bind.error,
        systemName.bind.errorVisible,
        antennaPort.value,
        antennaElecDowntilt.value,
        frequency.value,
        numChannels.value,
        antennaFrequency.value,
        antennaFrequency.bind.error,
        antennaPort.bind.error,
        antennaElecDowntilt.bind.error,
        txPower.value,
        attenuation.value,
        enabled.value,
        antennaVirtual.value,
    ];

    useValidation(value => !commonUtils.isBlank(value), [systemName]);

    const manageAntenna = async (splitName, value) => {
        if (splitName === "antennaFrequency") {
            const antennaData = await antennaRest.getAntennaData(sector.antennaName, null, value, null, null);
            antennaFrequency.set(antennaData.antennaFrequency);
            antennaPort.set(antennaData.antennaPort);
            antennaElecDowntilt.set(antennaData.antennaElecDowntilt);
            antennaVirtual.set(antennaData.antennaVirtual);
        } else if (splitName === "antennaPort") {
            const elecDowntilts = await antennaRest.getElecDowntilts(sector.antennaName, antennaFrequency.value, antennaPort.value);
            if (elecDowntilts.includes(antennaElecDowntilt.value)) {
                // Keep current selection if still valid
                antennaElecDowntilt.set(antennaElecDowntilt.value);
            } else {
                if (minMaxDowntilt === "min") {
                    const maxElecDowntilt = elecDowntilts.length > 0 ? Math.min(...elecDowntilts) : null;
                    antennaElecDowntilt.set(maxElecDowntilt);
                } else {
                    const maxElecDowntilt = elecDowntilts.length > 0 ? Math.max(...elecDowntilts) : null;
                    antennaElecDowntilt.set(maxElecDowntilt);
                }
            }
        }
    };

    useOnChange(async (name, value) => {
        const [splitName] = name.split("-");
        radio[splitName] = value;
        await manageAntenna(splitName, value);
        entryContext.setEntry(entry);
    }, [systemName, antennaFrequency, antennaPort, antennaElecDowntilt, antennaVirtual, frequency,
        numChannels, txPower, attenuation, enabled]);

    // the buttons live in the memoized markup, so they read the current entry instead of the rendered one
    // the sector stays even without radios
    const onDelete = () => {
        const entry = entryContext.getEntry();
        sector.radios.splice(sector.radios.indexOf(radio), 1);
        entryContext.setEntry(entry);
    };

    const onDuplicate = () => {
        const entry = entryContext.getEntry();
        const duplicate = { ...radio };
        delete duplicate.id;
        delete duplicate.key;
        antennaUtils.shiftColumns(entry, radio.column, 1);
        duplicate.column = radio.column + 1;
        sector.radios.push(duplicate);
        entryContext.setEntry(entry);
    };

    return useMemo(() =>
        <RowStyle className={enabled.value ? "" : "disabled"}>
            <td>
                <Checkbox {...enabled.bind} title={false} />
            </td>
            <td>
                <InputText {...systemName.bind} title={false} />
            </td>
            <td>
                <InputNumber {...frequency.bind} title={false} fractionDigits={2} textAlign="right" />
            </td>
            <td>
                <SelectSingle {...antennaFrequency.bind} title={false} disabled={antennaVirtual.value}>
                    {(async () => {
                        const frequencies = await antennaRest.getFrequencies(sector.antennaName);
                        return frequencies.map(frequency =>
                            <option key={frequency} value={frequency}>{frequency}</option>
                        );
                    })}
                </SelectSingle>
            </td>
            <td>
                <Tooltip message={antennaPort.value} enabled={!commonUtils.isBlank(antennaPort.value)}>
                    <TooltipFieldStyle>
                        <SelectSingle {...antennaPort.bind} title={false} disabled={antennaVirtual.value}>
                            {(async () => {
                                const ports = await antennaRest.getPorts(sector.antennaName, antennaFrequency.value);
                                return ports.map(port =>
                                    <option key={port} value={port}>{port}</option>
                                );
                            })}
                        </SelectSingle>
                    </TooltipFieldStyle>
                </Tooltip>
            </td>
            <td>
                <SelectSingle {...antennaElecDowntilt.bind} title={false} disabled={antennaVirtual.value}>
                    {(async () => {
                        const elecDowntilts = await antennaRest.getElecDowntilts(sector.antennaName, antennaFrequency.value, antennaPort.value);
                        return elecDowntilts.map(elecDowntilt =>
                            <option key={elecDowntilt} value={elecDowntilt}>{elecDowntilt}</option>
                        );
                    })}
                </SelectSingle>
            </td>
            <td>
                <InputNumber {...txPower.bind} title={false} fractionDigits={2} textAlign="right" />
            </td>
            <td>
                <InputNumber {...numChannels.bind} title={false} fractionDigits={0} textAlign="right" />
            </td>
            <td>
                <InputNumber {...attenuation.bind} title={false} fractionDigits={2} textAlign="right" />
            </td>
            <ActionsStyle>
                <div>
                    <Button svgIcon={mdiContentDuplicate} onClick={onDuplicate} compact />
                    <Button svgIcon={mdiTrashCanOutline} onClick={onDelete} compact />
                </div>
            </ActionsStyle>
        </RowStyle>, rerenderWhenChanged);
};
